import React from 'react';
import {
  SwipeableDrawer, Box, Typography, IconButton, List, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavItem } from './hubNavItems';

// Light lavender used for the active row background (Figma token primary-ash-50).
const ACTIVE_ROW_BG = '#F6F3F8';

type MoreSheetProps = {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
};

// Bottom sheet listing the Hub destinations that overflow the mobile bottom bar.
// Slides up from the bottom, shows a drag grabber, and can be dismissed (close button,
// backdrop, or swipe-down) without navigating. Selecting a row navigates and closes.
export default function MoreSheet({ open, onClose, items }: MoreSheetProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleSelect = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: 2,
        },
      }}
    >
      {/* Drag grabber handle */}
      <Box sx={{
        display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5,
      }}
      >
        <Box sx={{
          width: 32, height: 4, borderRadius: 2, bgcolor: 'grey.300',
        }}
        />
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 2, pr: 1, py: 1,
      }}
      >
        <Typography variant="h6">More</Typography>
        <IconButton onClick={onClose} aria-label="Close more menu">
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ py: 0 }}>
        {items.map((item) => {
          const selected = isActive(item.path);
          return (
            <ListItemButton
              key={item.name}
              onClick={() => handleSelect(item.path)}
              sx={{
                minHeight: 48,
                ...(selected && { bgcolor: ACTIVE_ROW_BG }),
                '&:hover': { bgcolor: selected ? ACTIVE_ROW_BG : 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <item.Icon sx={{ color: selected ? 'primary.main' : 'secondary.text' }} />
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  sx: {
                    color: selected ? 'primary.main' : 'text.primary',
                    fontWeight: selected ? 600 : 400,
                  },
                }}
              />
              {selected && <CheckIcon sx={{ color: 'primary.main' }} />}
            </ListItemButton>
          );
        })}
      </List>
    </SwipeableDrawer>
  );
}
